// FIRST, ahead of React and of every app module: lib/errors runs Sentry.init at
// module scope, and an import evaluated before it — @ui/react, the router and
// its thirty screens — would throw on the way in with no handler installed. An
// import list is evaluated top to bottom, so this line's POSITION is the
// guarantee, not the installErrorReporting() call below.
import { installErrorReporting } from "./lib/errors";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";

import { PageHead, Shell, ToastProvider } from "@ui/react";
import { initAnalytics } from "./lib/analytics";
import { AuthProvider } from "./lib/auth";
import { isAuthConfigured, missingConfiguration } from "./lib/config";
import { router } from "./router";
// Order is load-bearing: styles.css (linked in index.html) declares the tokens,
// app.css builds the shared product primitives on them, and contractor.css adds
// layout for this site's own features. Anything visual that a second Shoku site
// would need belongs in app.css, not below it.
import "@ui/app.css";
import "./styles/contractor.css";

// The window/document listeners that feed the support buffer. Sentry itself is
// already up — it initialises when the module above is imported.
installErrorReporting();

// After error reporting, so a failure inside the SDK is captured, and BEFORE the
// configuration gate below — an environment that cannot boot is exactly the kind
// of thing worth seeing in the data. Deliberately not boot-blocking: it never
// throws and its result is never awaited, so analytics can never be the reason
// this app fails to render.
initAnalytics();

const container = document.getElementById("root");
if (!container) throw new Error("#root is missing from index.html");

/**
 * Refuse to boot rather than mislead.
 *
 * Without a Firebase web API key every sign-in fails as `auth/invalid-api-key`,
 * which a login form can only report as bad credentials — so a whole
 * environment being unconfigured looks, to the person in front of it, like they
 * have forgotten their password. Saying so plainly costs one screen and saves
 * the support conversation.
 */
function ConfigurationError({ missing }: { missing: string[] }) {
  return (
    <Shell variant="auth">
      <PageHead
        eyebrow="Configuration"
        title="This site isn't configured yet"
        lead={`Shoku can't start here because the following ${
          missing.length === 1 ? "value is" : "values are"
        } missing: ${missing.join(", ")}.`}
      />
      <p className="dim">
        Nothing is wrong with your account. Use the Shoku Contractor app in the meantime.
      </p>
    </Shell>
  );
}

const blocking = isAuthConfigured() ? [] : missingConfiguration();

createRoot(container).render(
  <StrictMode>
    {blocking.length > 0 ? (
      <ConfigurationError missing={blocking} />
    ) : (
      <AuthProvider>
        {/* The transient channel, mounted once: sign-out and every "saved"
            confirmation goes through it rather than through a Banner that a
            person has to dismiss by hand. */}
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </AuthProvider>
    )}
  </StrictMode>,
);
